/**
 * Mock Data for Development - realistic mock data for UI development and testing.
 */
import { addDays, subDays, format, addHours } from 'date-fns'

export interface MockParticipant {
  id: string
  name: string
  email: string
  role: string
}

export interface MockAgendaItem {
  id: string
  title: string
  duration: number
  order: number
}

export interface MockMeeting {
  id: string
  title: string
  description: string
  startTime: string
  endTime: string
  duration: number
  status: string
  meetingUrl: string
  participants: MockParticipant[]
  agenda: MockAgendaItem[]
  governanceSettings: Record<string, unknown>
  createdAt: string
  /** Dashboard dual-agenda UX */
  audienceMode?: 'internal' | 'external'
  analytics?: Record<string, unknown>
}

export const mockMeetings: MockMeeting[] = [
  {
    id: 'meeting_001',
    title: 'Weekly Team Standup',
    description: 'Regular sync to discuss progress and blockers',
    startTime: addHours(new Date(), 2).toISOString(),
    endTime: addHours(new Date(), 2.5).toISOString(),
    duration: 30,
    status: 'scheduled',
    meetingUrl: 'https://zoom.us/j/123456789',
    participants: [
      { id: 'p1', name: 'Sarah Chen', email: 'sarah@example.com', role: 'host' },
      { id: 'p2', name: 'Mike Johnson', email: 'mike@example.com', role: 'participant' },
      { id: 'p3', name: 'Emily Davis', email: 'emily@example.com', role: 'participant' },
    ],
    agenda: [
      { id: 'a1', title: 'Review previous action items', duration: 5, order: 0 },
      { id: 'a2', title: 'Individual updates', duration: 15, order: 1 },
      { id: 'a3', title: 'Blockers and dependencies', duration: 5, order: 2 },
      { id: 'a4', title: 'Next steps', duration: 5, order: 3 },
    ],
    governanceSettings: {
      talkEquity: { enabled: true, targetRatio: 0.25 },
      interruptionPrevention: { enabled: true, threshold: 3 },
      agendaEnforcement: { enabled: true, strictMode: false },
    },
    createdAt: subDays(new Date(), 7).toISOString(),
  },
  {
    id: 'meeting_002',
    title: 'Q4 Planning Session',
    description: 'Strategic planning for Q4 initiatives',
    startTime: addDays(new Date(), 1).toISOString(),
    endTime: addHours(addDays(new Date(), 1), 2).toISOString(),
    duration: 120,
    status: 'scheduled',
    meetingUrl: 'https://zoom.us/j/987654321',
    participants: [
      { id: 'p1', name: 'Sarah Chen', email: 'sarah@example.com', role: 'host' },
      { id: 'p4', name: 'Alex Thompson', email: 'alex@example.com', role: 'participant' },
      { id: 'p5', name: 'Jordan Lee', email: 'jordan@example.com', role: 'participant' },
      { id: 'p6', name: 'Chris Martin', email: 'chris@example.com', role: 'participant' },
    ],
    agenda: [
      { id: 'a1', title: 'Q3 Review', duration: 20, order: 0 },
      { id: 'a2', title: 'Market Analysis', duration: 30, order: 1 },
      { id: 'a3', title: 'Q4 Goals & OKRs', duration: 40, order: 2 },
      { id: 'a4', title: 'Resource Allocation', duration: 20, order: 3 },
      { id: 'a5', title: 'Action Items & Next Steps', duration: 10, order: 4 },
    ],
    governanceSettings: {
      talkEquity: { enabled: true, targetRatio: 0.2 },
      interruptionPrevention: { enabled: true, threshold: 2 },
      agendaEnforcement: { enabled: true, strictMode: true },
    },
    createdAt: subDays(new Date(), 3).toISOString(),
  },
  {
    id: 'meeting_003',
    title: 'Design Review',
    description: 'Review new dashboard designs and gather feedback',
    startTime: subDays(new Date(), 1).toISOString(),
    endTime: addHours(subDays(new Date(), 1), 1).toISOString(),
    duration: 60,
    status: 'completed',
    meetingUrl: 'https://zoom.us/j/555666777',
    participants: [
      { id: 'p2', name: 'Mike Johnson', email: 'mike@example.com', role: 'host' },
      { id: 'p3', name: 'Emily Davis', email: 'emily@example.com', role: 'participant' },
      { id: 'p7', name: 'Taylor Swift', email: 'taylor@example.com', role: 'participant' },
    ],
    agenda: [
      { id: 'a1', title: 'Present new designs', duration: 20, order: 0 },
      { id: 'a2', title: 'Feedback round', duration: 30, order: 1 },
      { id: 'a3', title: 'Prioritization', duration: 10, order: 2 },
    ],
    analytics: {
      totalDuration: 58,
      participantStats: { p2: { talkTime: 25, interruptions: 1 }, p3: { talkTime: 18, interruptions: 0 }, p7: { talkTime: 15, interruptions: 2 } },
      agendaAdherence: 0.92,
      talkEquityScore: 0.85,
    },
    governanceSettings: {
      talkEquity: { enabled: true, targetRatio: 0.33 },
      interruptionPrevention: { enabled: true, threshold: 3 },
      agendaEnforcement: { enabled: false },
    },
    createdAt: subDays(new Date(), 5).toISOString(),
  },
]

export const mockDashboardAnalytics = {
  period: '7d',
  summary: { totalMeetings: 12, totalDuration: 720, averageDuration: 60, meetingsWithRithmo: 8, participantsManaged: 34 },
  talkEquity: {
    averageScore: 0.78,
    trend: '+5%',
    meetings: [
      { date: subDays(new Date(), 6).toISOString(), score: 0.72 },
      { date: subDays(new Date(), 5).toISOString(), score: 0.75 },
      { date: subDays(new Date(), 4).toISOString(), score: 0.74 },
      { date: subDays(new Date(), 3).toISOString(), score: 0.79 },
      { date: subDays(new Date(), 2).toISOString(), score: 0.82 },
      { date: subDays(new Date(), 1).toISOString(), score: 0.85 },
      { date: new Date().toISOString(), score: 0.78 },
    ],
  },
  interruptions: { totalPrevented: 23, averagePerMeeting: 2.9, reduction: '-18%' },
  agendaAdherence: { averageScore: 0.86, onTimeCompletions: 9, overtimeMeetings: 3 },
  topParticipants: [
    { name: 'Sarah Chen', meetings: 8, avgTalkTime: 12 },
    { name: 'Mike Johnson', meetings: 6, avgTalkTime: 15 },
    { name: 'Emily Davis', meetings: 5, avgTalkTime: 10 },
  ],
}

export const mockUserSettings = {
  profile: { name: 'Demo User', email: 'demo@rithmo.ai', avatar: null, timezone: 'America/New_York' },
  notifications: { meetingReminders: true, weeklyReport: true, postMeetingSummary: true },
  governance: {
    defaultTalkEquity: { enabled: true, targetRatio: 0.25 },
    defaultInterruptionPrevention: { enabled: true, threshold: 3 },
    defaultAgendaEnforcement: { enabled: true, strictMode: false },
  },
  integrations: { google: { connected: false }, outlook: { connected: false }, zoom: { connected: false }, slack: { connected: false } },
}

export function getMeetingById(id: string): MockMeeting | null {
  return mockMeetings.find((m) => m.id === id) ?? null
}

export function getUpcomingMeetings(): MockMeeting[] {
  const now = new Date()
  return mockMeetings
    .filter((m) => new Date(m.startTime) > now && m.status === 'scheduled')
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
}

export function getPastMeetings(): MockMeeting[] {
  return mockMeetings
    .filter((m) => m.status === 'completed')
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
}

export function getMeetingsByDate(date: Date | string): MockMeeting[] {
  const targetDate = format(new Date(date), 'yyyy-MM-dd')
  return mockMeetings.filter((m) => format(new Date(m.startTime), 'yyyy-MM-dd') === targetDate)
}

export function generateMockMeeting(overrides: Partial<MockMeeting> = {}): MockMeeting {
  const id = 'meeting_' + Date.now()
  const startTime = new Date()
  return {
    id,
    title: 'New Meeting',
    description: '',
    startTime: startTime.toISOString(),
    endTime: addHours(startTime, 1).toISOString(),
    duration: 60,
    status: 'scheduled',
    meetingUrl: '',
    participants: [],
    agenda: [],
    governanceSettings: {
      talkEquity: { enabled: true, targetRatio: 0.25 },
      interruptionPrevention: { enabled: true, threshold: 3 },
      agendaEnforcement: { enabled: true, strictMode: false },
    },
    audienceMode: 'internal',
    createdAt: new Date().toISOString(),
    ...overrides,
  }
}

export default {
  meetings: mockMeetings,
  dashboardAnalytics: mockDashboardAnalytics,
  userSettings: mockUserSettings,
  getMeetingById,
  getUpcomingMeetings,
  getPastMeetings,
  getMeetingsByDate,
  generateMockMeeting,
}
